/**
 * AfikimSocket — Unified Socket.IO client for Afikim CRM.
 *
 * Manages connections to multiple namespaces with:
 * - Auto-reconnection with jitter (prevents thundering herd)
 * - Session token authentication
 * - Connection state tracking
 * - Graceful cleanup
 *
 * Usage:
 *   import { afSocket } from '../utils/socket';
 *
 *   // After login:
 *   afSocket.connect('/notifications', sessionToken);
 *   afSocket.on('/notifications', 'new_notification', (data) => { ... });
 *
 *   // On logout:
 *   afSocket.disconnectAll();
 */

import { io } from 'socket.io-client';

class AfikimSocket {
  constructor() {
    this.sockets = {};       // namespace -> socket instance
    this.listeners = {};     // namespace -> [{event, callback}]
    this.state = {};         // namespace -> 'connected' | 'connecting' | 'disconnected' | 'error'
    this._stateCallbacks = new Set();  // state change listeners
  }

  /**
   * Connect to a namespace.
   * @param {string} namespace - e.g., '/notifications', '/chat'
   * @param {string} token - Session token for authentication
   * @returns {object} Socket.IO socket instance
   */
  connect(namespace, token) {
    if (this.sockets[namespace]) {
      // Already connected or connecting
      return this.sockets[namespace];
    }

    this._setState(namespace, 'connecting');

    const socket = io(namespace, {
      auth: { token },
      transports: ['websocket', 'polling'],  // WebSocket first, polling fallback
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: Infinity,
      randomizationFactor: 0.5,  // Jitter to prevent thundering herd
      timeout: 20000,
    });

    socket.on('connect', () => {
      this._setState(namespace, 'connected');
      console.log(`[Socket] Connected to ${namespace}`);

      // Replay any queued listeners
      if (this.listeners[namespace]) {
        this.listeners[namespace].forEach(({ event, callback }) => {
          socket.on(event, callback);
        });
        this.listeners[namespace] = [];
      }
    });

    socket.on('disconnect', (reason) => {
      this._setState(namespace, 'disconnected');
      console.log(`[Socket] Disconnected from ${namespace}: ${reason}`);
    });

    socket.on('connect_error', (err) => {
      this._setState(namespace, 'error');
      // Don't spam console — reconnection is automatic
      if (err.message !== 'xhr poll error') {
        console.warn(`[Socket] ${namespace} error:`, err.message);
      }
    });

    this.sockets[namespace] = socket;
    return socket;
  }

  /**
   * Listen for an event on a namespace.
   * If not yet connected, queues the listener for when connection happens.
   */
  on(namespace, event, callback) {
    const socket = this.sockets[namespace];
    if (socket && socket.connected) {
      socket.on(event, callback);
    } else {
      // Queue for later
      if (!this.listeners[namespace]) this.listeners[namespace] = [];
      this.listeners[namespace].push({ event, callback });
    }
  }

  /**
   * Remove event listener.
   */
  off(namespace, event, callback) {
    const socket = this.sockets[namespace];
    if (socket) {
      if (callback) {
        socket.off(event, callback);
      } else {
        socket.off(event);
      }
    }
    // Also remove from queue
    if (this.listeners[namespace]) {
      this.listeners[namespace] = this.listeners[namespace].filter(
        l => !(l.event === event && (!callback || l.callback === callback))
      );
    }
  }

  /**
   * Emit an event to the server.
   * @returns {boolean} true if sent, false if not connected
   */
  emit(namespace, event, data) {
    const socket = this.sockets[namespace];
    if (!socket || !socket.connected) {
      console.warn(`[Socket] Cannot emit ${event} to ${namespace}: not connected`);
      return false;
    }
    socket.emit(event, data);
    return true;
  }

  /**
   * Disconnect from a specific namespace.
   */
  disconnect(namespace) {
    const socket = this.sockets[namespace];
    if (socket) {
      socket.disconnect();
      delete this.sockets[namespace];
      delete this.listeners[namespace];
      this._setState(namespace, 'disconnected');
    }
  }

  /**
   * Disconnect from all namespaces (call on logout).
   */
  disconnectAll() {
    Object.keys(this.sockets).forEach(ns => this.disconnect(ns));
  }

  /**
   * Check if connected to a namespace.
   */
  isConnected(namespace) {
    return this.state[namespace] === 'connected';
  }

  /**
   * Get connection state for a namespace.
   */
  getState(namespace) {
    return this.state[namespace] || 'disconnected';
  }

  /**
   * Subscribe to state changes (for React components).
   * @param {function} callback - Called with (namespace, newState)
   * @returns {function} unsubscribe function
   */
  onStateChange(callback) {
    this._stateCallbacks.add(callback);
    return () => this._stateCallbacks.delete(callback);
  }

  _setState(namespace, newState) {
    const oldState = this.state[namespace];
    if (oldState === newState) return;
    this.state[namespace] = newState;
    this._stateCallbacks.forEach(cb => {
      try { cb(namespace, newState); } catch (e) { /* ignore */ }
    });
  }
}

// Singleton instance
export const afSocket = new AfikimSocket();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    afSocket.disconnectAll();
  });
}
